import { QueryFilter } from '@play-co/replicant';
import { createAsyncGetters } from '../../createAsyncGetters';
import {
  getBuyEstimate,
  getBuyPointEstimate,
  getMeme,
  getSellEstimate,
} from './tradingMeme.getters';
import {
  TradingIndexSchema,
  TradingSearchResult,
} from './tradingMeme.properties';
import { memeSearchCount, season2StartTime } from './tradingMeme.ruleset';
import { IAPConfig } from './types';
import { TradingMemeStatus } from './tradingMeme.schema';
import { HP } from '../../lib/HighPrecision';

export type TradingTokenSearchField =
  | 'availableAt'
  | 'top'
  | 'volume'
  | 'pointSupply'
  | 'tokenSupply';

export const tradingMemeAsyncGetters = createAsyncGetters({
  getMeme: async ({ memeId }: { memeId: string }, api) => {
    const memeState = await api.sharedStates.tradingMeme.fetch(memeId);
    return memeState && getMeme(memeState.global, memeId);
  },

  // IAP
  getInvoiceLink: async (payload: IAPConfig, api) => {
    return api.telegram.createInvoiceLink(payload);
  },
  getPurchaseConfirmed: async ({ productId }: { productId: string }, api) => {
    const unconsumedPurchases = await api.purchases.getUnconsumedPurchases();
    return unconsumedPurchases[productId];
  },

  getHotMeme: async (
    {
      field,
      unwantedTokenIds,
    }: {
      field: 'availableAt' | 'lastTx.createdAt';
      unwantedTokenIds: string[];
    },
    api,
  ) => {
    const results = (
      await api.sharedStates.tradingMeme.search({
        where: {
          id: {
            isNotOneOf: unwantedTokenIds,
          },
          status: {
            isAnyOf: [
              TradingMemeStatus.Moderated,
              TradingMemeStatus.ModeratedOS,
            ],
          },
        },
        sort: [{ field, order: 'desc' }],
        limit: 1,
      })
    ).results;

    if (results.length === 0) {
      return;
    }

    return results[0] as TradingSearchResult;
  },

  getMemesFromOpenSearch: async (
    searchParams: {
      memeIds: string[];
      status?: string[];
      availableAt?: number;
    },
    api,
  ) => {
    type Condition = {
      id: { isOneOf: string[] };
      status: { isAnyOf: string[] };
      availableAt: { greaterThanOrEqual?: number };
    };

    const condition: Condition = {
      id: {
        isOneOf: searchParams.memeIds,
      },
      status: {
        isAnyOf: searchParams.status
          ? searchParams.status
          : [
              TradingMemeStatus.Created,
              TradingMemeStatus.Moderated,
              TradingMemeStatus.ModeratedOS,
              TradingMemeStatus.Reported,
            ],
      },
      availableAt: {
        greaterThanOrEqual: searchParams.availableAt || 0,
      },
    };

    const searchProps = {
      where: condition,
      limit: searchParams.memeIds.length,
    };

    let results: TradingSearchResult[] = [];
    results = (await api.sharedStates.tradingMeme.search(searchProps)).results;
    return results;
  },

  searchMemes: async (
    {
      searchString,
      field,
      lessThan,
      availableAfterIfEqual,
      limit = memeSearchCount,
      randomSort = false,
    }: {
      searchString: string | undefined;
      field: TradingTokenSearchField;
      lessThan?: number;
      availableAfterIfEqual?: number | undefined;
      limit?: number;
      randomSort?: boolean;
    },
    api,
  ) => {
    let results: TradingSearchResult[] = [];

    let conditions: QueryFilter<TradingIndexSchema>[] = [];

    const lessThanCondition: {
      availableAt?: { lessThan: number; greaterThanOrEqual: number };
      top?: { lessThan: number; greaterThanOrEqual: number };
      volume?: { lessThan: number; greaterThanOrEqual: number };
      pointSupply?: { lessThan: number; greaterThanOrEqual: number };
      tokenSupply?: { lessThan: number; greaterThanOrEqual: number };
    } = {};

    conditions.push(lessThanCondition);

    if (lessThan !== undefined) {
      if (field === 'availableAt') {
        lessThan = Math.round(lessThan);
      }

      lessThanCondition[field] = { lessThan: lessThan, greaterThanOrEqual: 0 };

      if (availableAfterIfEqual !== undefined && field !== 'availableAt') {
        const equalityCondition: {
          availableAt: { greaterThan: number };
          top?: { between: [number, number] };
          volume?: { between: [number, number] };
          pointSupply?: { between: [number, number] };
          tokenSupply?: { between: [number, number] };
        } = {
          availableAt: {
            greaterThan: Math.max(season2StartTime, availableAfterIfEqual),
          },
        };

        equalityCondition[field] = { between: [lessThan, lessThan] };

        conditions.push(equalityCondition);
      }
    }

    // add the moderation constraint
    conditions.forEach((condition) => {
      condition.status = {
        isAnyOf: [
          TradingMemeStatus.Moderated,
          TradingMemeStatus.ModeratedOS,
          TradingMemeStatus.Reported,
          // @TODO: Remove?
          TradingMemeStatus.Created,
        ],
      };
    });

    if (searchString) {
      // add the search constraint
      conditions = conditions.reduce((conditionsWithSearch, condition) => {
        conditionsWithSearch.push({
          ...condition,
          profile: { name: { matchesAnyOf: [searchString] } },
        });

        conditionsWithSearch.push({
          ...condition,
          profile: { creatorName: { matchesAnyOf: [searchString] } },
        });

        return conditionsWithSearch;
      }, [] as QueryFilter<TradingIndexSchema>[]);
    }

    type SearchProps = Parameters<
      typeof api.sharedStates.tradingMeme.search
    >[0];

    const searchProps: SearchProps = {
      // @note: not sure why type checker complains
      // @ts-ignore
      where: conditions,
      sort: randomSort
        ? 'random'
        : [
            { field, order: 'desc' },
            { field: 'availableAt', order: 'asc' },
          ],
      limit,
    };

    console.log({ searchProps });

    results = (await api.sharedStates.tradingMeme.search(searchProps)).results;

    return results;
  },

  getBuyOffchainMemeEstimate: async (
    {
      offchainTokenId,
      currencyAmount,
    }: { offchainTokenId: string; currencyAmount: string },
    api,
  ) => {
    const offchainTokenShareState = await api.sharedStates.tradingMeme.fetch(
      offchainTokenId,
    );
    if (offchainTokenShareState) {
      return getBuyEstimate(
        offchainTokenShareState.global,
        HP(currencyAmount),
      ).toString();
    }
    return undefined;
  },

  gtBuyOffchainMemeWithPointsEstimate: async (
    {
      offchainTokenId,
      currencyAmount,
      relativePointShare,
    }: {
      offchainTokenId: string;
      currencyAmount: string;
      relativePointShare: number;
    },
    api,
  ) => {
    const offchainTokenSharedState = await api.sharedStates.tradingMeme.fetch(
      offchainTokenId,
    );
    if (offchainTokenSharedState) {
      return getBuyPointEstimate(
        offchainTokenSharedState.global,
        HP(currencyAmount),
        relativePointShare,
      ).toString();
    }
    return undefined;
  },
  getSellOffchainMemeEstimate: async (
    {
      offchainTokenId,
      pointAmount,
    }: { offchainTokenId: string; pointAmount: string },
    api,
  ) => {
    const offchainTokenShareState = await api.sharedStates.tradingMeme.fetch(
      offchainTokenId,
    );
    if (offchainTokenShareState) {
      return getSellEstimate(
        offchainTokenShareState.global,
        HP(pointAmount),
      ).toString();
    }
    return undefined;
  },
  getMyMemes: async (
    {
      userId,
      createdMemes,
      ownedMemeIds,
    }: { userId: string; createdMemes: boolean; ownedMemeIds: string[] },
    api,
  ) => {
    const createdByMe = createdMemes
      ? api.sharedStates.tradingMeme.search({
          where: {
            profile: {
              creatorId: {
                isAnyOf: [userId],
              },
            },
            status: {
              isAnyOf: [
                TradingMemeStatus.Created,
                TradingMemeStatus.Moderated,
                TradingMemeStatus.ModeratedOS,
              ],
            },
          },
          limit: 100,
        })
      : Promise.resolve({ results: [] });

    const ownedByMe =
      ownedMemeIds.length > 0
        ? api.sharedStates.tradingMeme.search({
            where: {
              id: {
                isOneOf: ownedMemeIds,
              },
              status: {
                isAnyOf: [
                  TradingMemeStatus.Created,
                  TradingMemeStatus.Moderated,
                  TradingMemeStatus.ModeratedOS,
                ],
              },
            },
            limit: ownedMemeIds.length,
          })
        : Promise.resolve({ results: [] });

    return {
      created: (await createdByMe).results,
      owned: (await ownedByMe).results,
    };
  },
});
